import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '../../ui/icons';
import { OnboardingStep, useOnboarding } from '../../../contexts/OnboardingContext';
import { useTheme } from '@mui/material/styles';

type TooltipPositionType = 'top' | 'bottom';

export interface OnboardingTooltipProps {
  step: OnboardingStep;
  targetId?: string;
  title: string;
  description: string;
  position?: TooltipPositionType;
  showNext?: boolean;
  showExit?: boolean;
  showAddNow?: boolean;
  onNext?: () => void;
  onAddNow?: () => void;
}

const STEP_ORDER = [
  OnboardingStep.OVERVIEW_CHART,
  OnboardingStep.SPENDING_DISTRIBUTION,
  OnboardingStep.SPENDING_TRENDS,
  OnboardingStep.ASSET_OVERVIEW,
  OnboardingStep.CREDIT_CARD,
  OnboardingStep.LOAN_BOOK,
  OnboardingStep.ASSET_FLUCTUATION,
  OnboardingStep.ADD_TRANSACTION,
];

const TOTAL_STEPS = STEP_ORDER.length;
const HIGHLIGHT_PADDING = 0;
const TOOLTIP_GAP = 16;
const TOOLTIP_MARGIN = 16;

const OnboardingTooltip = ({
  step,
  targetId,
  title,
  description,
  position = 'bottom',
  showNext = true,
  showExit = false,
  showAddNow = false,
  onNext,
  onAddNow,
}: OnboardingTooltipProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['onboarding']);
  const {
    currentStep,
    nextStep,
    triggerCompletionModal,
    skipOnboarding,
    completeOnboarding,
  } = useOnboarding();
  const [positionStyles, setPositionStyles] = useState<Record<string, number | string>>({});

  const stepNumber = STEP_ORDER.indexOf(step) + 1;

  const calculatePosition = useCallback(() => {
    const defaultPosition = { top: 200, left: TOOLTIP_MARGIN, right: TOOLTIP_MARGIN };

    if (!targetId) return defaultPosition;

    // Use ~= selector to match space-separated words in data-onboarding-id
    // Find ALL matching elements and calculate combined bounding box
    const elements = document.querySelectorAll(`[data-onboarding-id~="${targetId}"]`);
    if (elements.length === 0) return defaultPosition;

    // Calculate combined bounding box for all matching elements
    let minTop = Infinity;
    let maxBottom = -Infinity;

    elements.forEach((element) => {
      const boundingRect = element.getBoundingClientRect();
      minTop = Math.min(minTop, boundingRect.top);
      maxBottom = Math.max(maxBottom, boundingRect.bottom);
    });

    // Small extra gap on ADD_TRANSACTION to clear the circular spotlight padding
    const extraGap = step === OnboardingStep.ADD_TRANSACTION ? 6 : 0;

    if (position === 'top') {
      return {
        bottom: window.innerHeight - minTop + HIGHLIGHT_PADDING + TOOLTIP_GAP + extraGap,
        left: TOOLTIP_MARGIN,
        right: TOOLTIP_MARGIN,
      };
    }

    return {
      top: maxBottom + HIGHLIGHT_PADDING + TOOLTIP_GAP,
      left: TOOLTIP_MARGIN,
      right: TOOLTIP_MARGIN,
    };
  }, [targetId, position, step]);

  useEffect(() => {
    const updatePosition = () => {
      const newStyles = calculatePosition();
      setPositionStyles(prev => {
        // Only update if values actually changed to prevent infinite loops
        if (JSON.stringify(prev) === JSON.stringify(newStyles)) {
          return prev;
        }
        return newStyles;
      });
    };

    const timeoutId = setTimeout(updatePosition, 150);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    // Remove MutationObserver as it causes infinite loops
    // The scroll and resize listeners should be sufficient

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [calculatePosition]);

  if (currentStep !== step) return null;

  const handleNext = () => (onNext ? onNext() : nextStep());

  // On the final step the tour is already done — X should finish immediately
  // instead of routing through the "exit sample data" confirmation modal,
  // which otherwise requires a second click to truly dismiss.
  const handleClose = () =>
    step === OnboardingStep.ADD_TRANSACTION
      ? completeOnboarding()
      : triggerCompletionModal();

  const caretStyles = {
    content: '""',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    ...(position === 'bottom'
      ? { top: -10, borderBottom: `10px solid ${theme.palette.navy[900]}` }
      : { bottom: -10, borderTop: `10px solid ${theme.palette.navy[900]}` }),
  };

  return (
    <Box sx={{ position: 'fixed', zIndex: theme.zIndex.modal + 10, ...positionStyles }}>
      <Paper
        elevation={8}
        sx={{
          p: 2.5,
          borderRadius: 3,
          bgcolor: 'navy.900',
          color: 'common.white',
          position: 'relative',
          '&::before': caretStyles,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="body2">
            {stepNumber}/{TOTAL_STEPS}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: 'common.white', p: 0.5, mr: -1, mt: -1 }}
          >
            <CloseIcon style={{ fontSize: 22 }} />
          </IconButton>
        </Stack>

        <Typography variant="subtitle1" fontWeight={700} color="common.white" mb={1}>
          {title}
        </Typography>

        <Typography lineHeight={1.6} mb={2}>
          {description}
        </Typography>

        {showNext && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              variant="text"
              size="small"
              onClick={skipOnboarding}
              sx={{
                color: 'grey.400',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {t('skip')}
            </Button>
            <Stack direction="row" spacing={1.5}>
              {showAddNow && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onAddNow}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 6,
                    borderColor: 'grey.200',
                    color: 'common.white',
                    textTransform: 'none',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: 'grey.300', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  {t('add_now')}
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleNext}
                sx={{
                  px: showAddNow ? 2 : 6,
                  bgcolor: 'common.white',
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                }}
              >
                {showExit ? t('finish') : t('next')}
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default OnboardingTooltip;
